import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

type Language = "pt" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  pt: {
    // Navigation
    "nav.home": "Início",
    "nav.manifesto": "Comunicações",
    "nav.program": "Programa",
    "nav.lists": "Listas A",
    "nav.calendar": "Calendário",
    "nav.documents": "Documentos",
    "nav.news": "Notícias",
    "nav.contact": "Participa",
    "nav.joinUs": "Junta-te a Nós",

    // Lists submenu
    "nav.lists.management": "Órgãos de Gestão",
    "nav.lists.scientific": "Conselho Científico",
    "nav.lists.pedagogical": "Conselho Pedagógico",

    // Axes
    "axis.1": "Motivar e Valorizar Talento",
    "axis.2": "Inovação e Transformação Digital",
    "axis.3": "Excelência na Investigação",
    "axis.4": "Excelência no Ensino",
    "axis.5": "Impacto Internacional e Acreditações",
    "axis.6": "Bem-Estar e Impacto Ambiental",
    "axis.7": "Bolsas de Estudo e Incentivos",
    "axis.8": "Modernizar as Instalações",
    "axis.9": "ULisboa e Parcerias",
    "axis.10": "Eventos Científicos, Culturais e Desportivos",

    // Common
    "common.readMore": "Ler Mais",
    "common.download": "Descarregar",
    "common.share": "Partilhar",
    "common.back": "Voltar",
    "common.next": "Próximo",
    "common.previous": "Anterior",
    "common.seeAll": "Ver Todos",
    "common.loading": "A carregar...",
    "common.placeholder": "[Conteúdo a definir]",

    // Home
    //    'home.hero.subtitle': 'Candidatura à Presidência do ISEG 2026-2030',
    "home.hero.subtitle": "ISEG com propósito: excelência, inclusão e futuro.",
    "home.why.title": "Propósito",
    "home.challenges.title": "Principais Desafios",
    "home.axes.title": "Princípios Orientadores",
    "home.cta.title": "Conhece o Programa",
    "home.join.title": "Junta-te a Nós",

    // Manifesto
    "manifesto.title": "Manifesto",
    "manifesto.purpose": "Propósito",
    "manifesto.challenges": "Desafios",
    "manifesto.principles": "Princípios Orientadores",

    // Calendar
    "calendar.title": "Calendário Eleitoral",
    "calendar.nextStep": "Próxima Etapa",

    // Documents
    "documents.title": "Documentos e Regulamentos",

    // News
    "news.title": "Notícias",
    "news.related": "Notícias Relacionadas",

    // Contact
    "contact.title": "Contactos",
    "contact.form.name": "Nome",
    "contact.form.email": "Email",
    "contact.form.subject": "Assunto",
    "contact.form.message": "Mensagem",
    "contact.form.send": "Enviar",

    // Form
    "form.error.required": "Este campo é obrigatório",
    "form.error.invalidEmail": "Endereço de email inválido",
    "form.error.rateLimit": "Por favor aguarda um momento antes de enviar novamente",
    "form.error.generic": "Ocorreu um erro ao enviar. Por favor tenta novamente.",
    "form.sending": "A enviar...",
    "form.success.title": "Mensagem enviada!",
    "form.success.message": "Obrigado pela tua participação. Responderemos o mais brevemente possível.",
    "form.success.sendAnother": "Enviar outra mensagem",

    // Footer
    "footer.rights": "Todos os direitos reservados",
    "footer.campaign": "Candidatura Mário Caldeira",
  },
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.manifesto": "News",
    "nav.program": "Program",
    "nav.lists": "Lists A",
    "nav.calendar": "Calendar",
    "nav.documents": "Documents",
    "nav.news": "News",
    "nav.contact": "Get Involved",
    "nav.joinUs": "Join Us",

    // Lists submenu
    "nav.lists.management": "Management Board",
    "nav.lists.scientific": "Scientific Board",
    "nav.lists.pedagogical": "Pedagogical Board",

    // Axes
    "axis.1": "Motivate and Value Talent",
    "axis.2": "Innovation and Digital Transformation",
    "axis.3": "Excellence in Research",
    "axis.4": "Excellence in Teaching",
    "axis.5": "International Impact and Accreditations",
    "axis.6": "Well-being and Environmental Impact",
    "axis.7": "Scholarships and Merit Incentives",
    "axis.8": "Modernize Facilities",
    "axis.9": "ULisboa and Partnerships",
    "axis.10": "Scientific, Cultural and Sports Events",

    // Common
    "common.readMore": "Read More",
    "common.download": "Download",
    "common.share": "Share",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.seeAll": "See All",
    "common.loading": "Loading...",
    "common.placeholder": "[Content to be defined]",

    // Home
    "home.hero.subtitle": "Candidacy for ISEG Presidency 2026-2030",
    "home.why.title": "Purpose",
    "home.challenges.title": "Key Challenges",
    "home.axes.title": "10 Strategic Axes",
    "home.cta.title": "Discover the Program",
    "home.join.title": "Join Us",

    // Manifesto
    "manifesto.title": "Manifesto",
    "manifesto.purpose": "Purpose",
    "manifesto.challenges": "Challenges",
    "manifesto.principles": "Guiding Principles",

    // Calendar
    "calendar.title": "Electoral Calendar",
    "calendar.nextStep": "Next Step",

    // Documents
    "documents.title": "Documents and Regulations",

    // News
    "news.title": "News",
    "news.related": "Related News",

    // Contact
    "contact.title": "Contact",
    "contact.form.name": "Name",
    "contact.form.email": "Email",
    "contact.form.subject": "Subject",
    "contact.form.message": "Message",
    "contact.form.send": "Send",

    // Form
    "form.error.required": "This field is required",
    "form.error.invalidEmail": "Invalid email address",
    "form.error.rateLimit": "Please wait a moment before sending again",
    "form.error.generic": "An error occurred while sending. Please try again.",
    "form.sending": "Sending...",
    "form.success.title": "Message sent!",
    "form.success.message": "Thank you for your participation. We will respond as soon as possible.",
    "form.success.sendAnother": "Send another message",

    // Footer
    "footer.rights": "All rights reserved",
    "footer.campaign": "Mário Caldeira Candidacy",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("pt");

  const t = useCallback(
    (key: string): string => {
      return translations[language][key] || key;
    },
    [language],
  );

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default useLanguage;
